import React, { useState, useEffect } from 'react';
import { useAuth } from '../store/AuthContext';
import { useToast } from '../store/ToastContext';
import { useTheme } from '../store/ThemeContext';

import { validateEmail, validatePhone, validatePasswordStrength } from '../utils/validation';
import { classNames } from '../utils/helpers';
import Card from '../components/Card';
import Input from '../components/Input';
import Button from '../components/Button';
import Toggle from '../components/Toggle';
import PasswordStrength from '../components/PasswordStrength';
import { UserIcon, LockIcon, SunIcon, MoonIcon, BellIcon, ShieldIcon, TrashIcon } from '../components/Icons';
import styles from './Profile.module.css';

export default function Profile() {
  const { user, updateUser, changePassword } = useAuth();
  const toast = useToast();
  const { theme, toggleTheme } = useTheme();

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone_number: '',
    company_name: '',
    gstin: '',
    pan: '',
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [notifications, setNotifications] = useState({
    email: true,
    expiryReminders: true,
    paymentReceipts: true,
    promotional: false,
  });

  const [security, setSecurity] = useState({
    twoFactor: false,
    sessionAlerts: true,
  });

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone_number: user.phone_number || user.phone || '',
        company_name: user.company_name || user.company || '',
        gstin: user.gstin || '',
        pan: user.pan || '',
      });
    }
  }, [user]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    if (profileErrors[name]) {
      setProfileErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateProfile = () => {
    const errors = {};
    
    if (!profileData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!profileData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(profileData.email)) {
      errors.email = 'Please enter a valid email';
    }
    
    if (profileData.phone_number && !validatePhone(profileData.phone_number)) {
      errors.phone_number = 'Please enter a valid phone number';
    }
    
    const gstin = profileData.gstin.trim().toUpperCase();
    if (gstin && !/^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gstin)) {
      errors.gstin = 'Please enter a valid 15-character GSTIN';
    }

    const pan = profileData.pan.trim().toUpperCase();
    if (pan && !/^[A-Z]{5}\d{4}[A-Z]$/.test(pan)) {
      errors.pan = 'Please enter a valid 10-character PAN';
    }

    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePassword = () => {
    const errors = {};
    
    if (!passwordData.current_password) {
      errors.current_password = 'Current password is required';
    }
    
    if (!passwordData.new_password) {
      errors.new_password = 'New password is required';
    } else {
      const strength = validatePasswordStrength(passwordData.new_password);
      if (strength.score < 3) {
        errors.new_password = 'Please choose a stronger password';
      }
    }
    
    if (!passwordData.confirm_password) {
      errors.confirm_password = 'Please confirm your new password';
    } else if (passwordData.new_password !== passwordData.confirm_password) {
      errors.confirm_password = 'Passwords do not match';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!validateProfile()) return;
    
    setProfileLoading(true);
    try {
      await updateUser({
        name: profileData.name,
        phone_number: profileData.phone_number,
        company_name: profileData.company_name,
        gstin: profileData.gstin.trim(),
        pan: profileData.pan.trim(),
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;

    setPasswordLoading(true);
    try {
      await changePassword(passwordData);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
      toast.success('Password changed successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleNotificationChange = (key) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
    toast.success('Notification preference updated');
  };

  const handleSecurityChange = (key) => {
    setSecurity(prev => ({ ...prev, [key]: !prev[key] }));
    toast.success('Security setting updated');
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Account deletion request submitted');
      setShowDeleteConfirm(false);
    } catch (error) {
      toast.error('Failed to process request');
    } finally {
      setDeleteLoading(false);
    }
  };

  const TABS = [
    { id: 'personal', label: 'Personal Info' },
    { id: 'password', label: 'Password' },
    { id: 'preferences', label: 'Preferences' },
    { id: 'security', label: 'Security' },
  ];
  const [activeTab, setActiveTab] = useState('personal');

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Profile</h1>
        <p className={styles.subtitle}>Manage your account information</p>
      </div>

      <div className={styles.tabs}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={classNames(styles.tab, activeTab === tab.id && styles.tabActive)}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.sections}>
        {activeTab === 'personal' && (
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <UserIcon />
            </div>
            <div>
              <h2 className={styles.cardTitle}>Personal Information</h2>
              <p className={styles.cardDescription}>Your basic profile details</p>
            </div>
          </div>
          
          <form onSubmit={handleProfileSubmit} className={styles.form}>
            <div className={styles.formGrid}>
              <Input
                label="Full Name"
                name="name"
                value={profileData.name}
                onChange={handleProfileChange}
                error={profileErrors.name}
                placeholder="Enter your full name"
              />
              <Input
                label="Email Address"
                name="email"
                type="email"
                value={profileData.email}
                onChange={handleProfileChange}
                error={profileErrors.email}
                placeholder="Enter your email"
              />
              <Input
                label="Phone Number"
                name="phone_number"
                value={profileData.phone_number}
                onChange={handleProfileChange}
                error={profileErrors.phone_number}
                placeholder="Enter your phone number"
              />
              <Input
                label="Company Name"
                name="company_name"
                value={profileData.company_name}
                onChange={handleProfileChange}
                placeholder="Enter your company name (optional)"
              />
              <Input
                label="GSTIN"
                name="gstin"
                value={profileData.gstin}
                onChange={handleProfileChange}
                error={profileErrors.gstin}
                placeholder="Shown on your tax invoices (optional)"
                maxLength={15}
              />
              <Input
                label="PAN"
                name="pan"
                value={profileData.pan}
                onChange={handleProfileChange}
                error={profileErrors.pan}
                placeholder="10-character PAN (GSTIN or PAN is required to pay)"
                maxLength={10}
              />
            </div>
            
            <div className={styles.formActions}>
              <Button type="submit" loading={profileLoading}>
                Save Changes
              </Button>
            </div>
          </form>
        </Card>
        )}

        {activeTab === 'password' && (
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <LockIcon />
            </div>
            <div>
              <h2 className={styles.cardTitle}>Change Password</h2>
              <p className={styles.cardDescription}>Update your account password</p>
            </div>
          </div>
          
          <form onSubmit={handlePasswordSubmit} className={styles.form}>
            <div className={styles.formStack}>
              <Input
                label="Current Password"
                name="current_password"
                type="password"
                value={passwordData.current_password}
                onChange={handlePasswordChange}
                error={passwordErrors.current_password}
                placeholder="Enter your current password"
              />
              <Input
                label="New Password"
                name="new_password"
                type="password"
                value={passwordData.new_password}
                onChange={handlePasswordChange}
                error={passwordErrors.new_password}
                placeholder="Enter your new password"
              />
              {passwordData.new_password && (
                <PasswordStrength password={passwordData.new_password} />
              )}
              <Input
                label="Confirm New Password"
                name="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={handlePasswordChange}
                error={passwordErrors.confirm_password}
                placeholder="Confirm your new password"
              />
            </div>
            
            <div className={styles.formActions}>
              <Button type="submit" loading={passwordLoading}>
                Update Password
              </Button>
            </div>
          </form>
        </Card>
        )}

        {activeTab === 'preferences' && (
        <>
        {/* Appearance */}
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              {theme === 'dark' ? <MoonIcon /> : <SunIcon />}
            </div>
            <div>
              <h2 className={styles.cardTitle}>Appearance</h2>
              <p className={styles.cardDescription}>Customize how the app looks</p>
            </div>
          </div>

          <div className={styles.settingItem}>
            <div className={styles.settingInfo}>
              <span className={styles.settingLabel}>Dark Mode</span>
              <span className={styles.settingDescription}>
                Switch between light and dark themes
              </span>
            </div>
            <Toggle
              checked={theme === 'dark'}
              onChange={toggleTheme}
            />
          </div>
        </Card>

        {/* Notifications */}
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <BellIcon />
            </div>
            <div>
              <h2 className={styles.cardTitle}>Notifications</h2>
              <p className={styles.cardDescription}>Manage your notification preferences</p>
            </div>
          </div>

          <div className={styles.settingsList}>
            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <span className={styles.settingLabel}>Email Notifications</span>
                <span className={styles.settingDescription}>
                  Receive important updates via email
                </span>
              </div>
              <Toggle
                checked={notifications.email}
                onChange={() => handleNotificationChange('email')}
              />
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <span className={styles.settingLabel}>Expiry Reminders</span>
                <span className={styles.settingDescription}>
                  Get notified before your subscription expires
                </span>
              </div>
              <Toggle
                checked={notifications.expiryReminders}
                onChange={() => handleNotificationChange('expiryReminders')}
              />
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <span className={styles.settingLabel}>Payment Receipts</span>
                <span className={styles.settingDescription}>
                  Receive receipts for payments via email
                </span>
              </div>
              <Toggle
                checked={notifications.paymentReceipts}
                onChange={() => handleNotificationChange('paymentReceipts')}
              />
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <span className={styles.settingLabel}>Promotional Emails</span>
                <span className={styles.settingDescription}>
                  Receive offers and product updates
                </span>
              </div>
              <Toggle
                checked={notifications.promotional}
                onChange={() => handleNotificationChange('promotional')}
              />
            </div>
          </div>
        </Card>
        </>
        )}

        {activeTab === 'security' && (
        <>
        {/* Security */}
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <ShieldIcon />
            </div>
            <div>
              <h2 className={styles.cardTitle}>Security</h2>
              <p className={styles.cardDescription}>Protect your account</p>
            </div>
          </div>

          <div className={styles.settingsList}>
            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <span className={styles.settingLabel}>Two-Factor Authentication</span>
                <span className={styles.settingDescription}>
                  Add an extra layer of security to your account
                </span>
              </div>
              <Toggle
                checked={security.twoFactor}
                onChange={() => handleSecurityChange('twoFactor')}
              />
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <span className={styles.settingLabel}>Session Login Alerts</span>
                <span className={styles.settingDescription}>
                  Get notified when your account is accessed from a new device
                </span>
              </div>
              <Toggle
                checked={security.sessionAlerts}
                onChange={() => handleSecurityChange('sessionAlerts')}
              />
            </div>
          </div>
        </Card>

        {/* Danger Zone */}
        <Card className={`${styles.card} ${styles.dangerCard}`}>
          <div className={styles.cardHeader}>
            <div className={`${styles.cardIcon} ${styles.dangerIcon}`}>
              <TrashIcon />
            </div>
            <div>
              <h2 className={styles.cardTitle}>Danger Zone</h2>
              <p className={styles.cardDescription}>Irreversible account actions</p>
            </div>
          </div>

          {!showDeleteConfirm ? (
            <div className={styles.dangerContent}>
              <p className={styles.dangerText}>
                Once you delete your account, there is no going back. All your data,
                subscriptions, and transaction history will be permanently removed.
              </p>
              <Button
                variant="danger"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete Account
              </Button>
            </div>
          ) : (
            <div className={styles.confirmDelete}>
              <p className={styles.confirmText}>
                Are you sure you want to delete your account? This action cannot be undone.
              </p>
              <div className={styles.confirmActions}>
                <Button
                  variant="secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  loading={deleteLoading}
                  onClick={handleDeleteAccount}
                >
                  Yes, Delete My Account
                </Button>
              </div>
            </div>
          )}
        </Card>
        </>
        )}
      </div>
    </div>
  );
}
